/**
 * Centralized API client for Healing Hues
 *
 * Implements the client side of public API contract v1
 * (healing-hues-workspace/docs/contracts/public-api-v1.md):
 *
 * - Base URL is the API origin only (config.apiBaseUrl); every path passed
 *   here starts with '/api/...'.
 * - Responses use the envelope {success, data} / {success:false, error:{code,message}}.
 *   The helpers below return `data`; failures reject with an ApiError.
 * - The owner token (sessionStorage 'hh_admin_token') is sent only on
 *   non-public paths. A 401 from a non-public path clears it and fires the
 *   window event 'hh:unauthorized'.
 *
 * @module apiClient
 */

import axios from 'axios';
import config from '../../config';
import ApiError, { ERROR_CODES } from '../errors/ApiError';
import { handleApiError, ERROR_MESSAGES } from '../errors/errorHandler';

/** sessionStorage key holding the owner JWT (contract §2). */
export const ADMIN_TOKEN_KEY = 'hh_admin_token';

/** Window event dispatched when a protected request returns 401 (contract §2). */
export const UNAUTHORIZED_EVENT = 'hh:unauthorized';

/** Paths reachable without a token (contract §2 allow-list). */
const PUBLIC_PATH_PREFIXES = ['/api/public/', '/api/health', '/api/auth/login'];

/**
 * Path part of a request URL, whether it is relative ('/api/x?y') or absolute.
 * @param {string} url
 * @returns {string}
 */
const pathOf = (url = '') => {
  try {
    return new URL(url, 'http://placeholder.invalid').pathname;
  } catch (e) {
    return String(url).split('?')[0];
  }
};

/**
 * @param {string} url
 * @returns {boolean} True when the contract lets this path be called anonymously.
 */
export const isPublicPath = (url) => {
  const path = pathOf(url);
  return PUBLIC_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(prefix));
};

/** @returns {string|null} The stored owner token. */
export const getAdminToken = () => {
  try {
    return sessionStorage.getItem(ADMIN_TOKEN_KEY);
  } catch (e) {
    return null;
  }
};

/** @param {string} token - Owner JWT from POST /api/auth/login */
export const setAdminToken = (token) => {
  try {
    sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
  } catch (e) {
    // Storage unavailable (private mode); the owner has to sign in again.
  }
};

export const clearAdminToken = () => {
  try {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch (e) {
    // Nothing stored.
  }
};

/**
 * Configured axios instance. Prefer the get/post/... helpers below, which
 * unwrap the envelope.
 */
const apiClient = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

apiClient.interceptors.request.use((requestConfig) => {
  const headers = requestConfig.headers || {};

  if (isPublicPath(requestConfig.url)) {
    // Never leak the owner token to public endpoints.
    if (typeof headers.delete === 'function') {
      headers.delete('Authorization');
    } else {
      delete headers.Authorization;
    }
  } else {
    const token = getAdminToken();
    if (token) {
      if (typeof headers.set === 'function') {
        headers.set('Authorization', `Bearer ${token}`);
      } else {
        headers.Authorization = `Bearer ${token}`;
      }
    }
  }
  requestConfig.headers = headers;

  if (config.isDevelopment) {
    // Method and path only: request bodies carry client PII.
    console.log(`API Request: ${requestConfig.method?.toUpperCase()} ${requestConfig.url}`);
  }

  return requestConfig;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const apiError = handleApiError(error);
    const url = error?.config?.url;

    if (apiError.status === 401 && !isPublicPath(url)) {
      clearAdminToken();
      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT, { detail: { url: pathOf(url) } }));
      }
    }

    if (config.isDevelopment && !apiError.isCanceled) {
      console.error('API Error:', apiError.toJSON());
    }

    return Promise.reject(apiError);
  }
);

/**
 * Return `body.data` from a success envelope.
 *
 * @param {import('axios').AxiosResponse|object} responseOrBody - axios response or parsed body
 * @returns {object} The envelope's data object
 * @throws {ApiError} invalid_response when the body is not a success envelope
 */
export const unwrap = (responseOrBody) => {
  const isAxiosResponse =
    responseOrBody && typeof responseOrBody === 'object' && 'status' in responseOrBody && 'data' in responseOrBody && 'headers' in responseOrBody;
  const body = isAxiosResponse ? responseOrBody.data : responseOrBody;
  const status = isAxiosResponse ? responseOrBody.status : 200;

  if (!body || typeof body !== 'object' || body.success !== true || !body.data || typeof body.data !== 'object') {
    throw new ApiError(
      ERROR_MESSAGES[ERROR_CODES.INVALID_RESPONSE],
      status,
      ERROR_CODES.INVALID_RESPONSE
    );
  }
  return body.data;
};

/**
 * GET and unwrap.
 * @param {string} url - Path starting with '/api/'
 * @param {object} [options]
 * @param {object} [options.params] - Query parameters
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>} Envelope data
 */
export const get = async (url, { params, signal } = {}) =>
  unwrap(await apiClient.get(url, { params, signal }));

/**
 * POST and unwrap.
 * @param {string} url - Path starting with '/api/'
 * @param {object} [body] - snake_case request body
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>} Envelope data
 */
export const post = async (url, body = {}, { signal } = {}) =>
  unwrap(await apiClient.post(url, body, { signal }));

/** PUT and unwrap. */
export const put = async (url, body = {}, { signal } = {}) =>
  unwrap(await apiClient.put(url, body, { signal }));

/** PATCH and unwrap. */
export const patch = async (url, body = {}, { signal } = {}) =>
  unwrap(await apiClient.patch(url, body, { signal }));

/** DELETE and unwrap. Pass `data` for a request body. */
export const del = async (url, { data, signal } = {}) =>
  unwrap(await apiClient.delete(url, { data, signal }));

export default apiClient;
